import React, {useState, useRef, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Link, useNavigate} from 'react-router-dom';
import {useToast} from './Toast';
import {BookIcon, CreditCardIcon, DownloadIcon, HomeIcon, LogOutIcon, PlayCircleIcon} from './Icons';
import {signOut as signOutThunk} from '../store/slices/authThunks';
import type {AppDispatch, RootState} from '../store/store';
import {isProSelector, planLabelSelector} from '../store/selectors/entitlement.selectors';

// Every row in the dropdown, so the marketing links sit flush with Account rather than being
// obviously bolted on.
const itemClass = 'flex w-full items-center gap-2.5 px-4 py-2 text-left text-sm text-gray-300 ' +
  'hover:bg-gray-600 hover:text-white transition-colors cursor-pointer';

const UserProfile: React.FC = () => {
  const {showToast} = useToast();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const {user, appUser} = useSelector((state: RootState) => state.auth);
  // Never read `appUser.subscribed` directly — entitlement has one owner. See entitlement.selectors.
  const isPro = useSelector(isProSelector);
  const planLabel = useSelector(planLabelSelector);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

    // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleSignOut = async () => {
    try {
      await dispatch(signOutThunk()).unwrap();
      showToast('Signed out successfully', 'success');
            // Protected routes now guard the app, so send the user somewhere public explicitly.
      navigate('/');
    } catch (error: any) {
      showToast(`Failed to sign out: ${error}`, 'error');
    }
    setIsDropdownOpen(false);
  };

  const handleAccountClick = () => {
    navigate('/account');
    setIsDropdownOpen(false);
  };

  const closeDropdown = () => setIsDropdownOpen(false);

    // Get user avatar from metadata or use default
  const avatar = user?.user_metadata?.avatar_url
    ? user.user_metadata.avatar_url
    : '👤';

  const displayName = appUser?.name || user?.email?.split('@')[0] || 'User';
  const email = user?.email || '';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="flex items-center space-x-2 px-2 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-700 rounded transition-colors cursor-pointer sm:px-3"
        title="Account Settings"
      >
        {user?.user_metadata?.avatar_url ? (
          <img
            src={user.user_metadata.avatar_url}
            alt="Avatar"
            className="w-6 h-6 rounded-full flex-shrink-0"
          />
        ) : (
          <span className="text-lg flex-shrink-0">{avatar}</span>
        )}
        {/* Name/email/plan only show once there's room for them — unbounded text here is what
            was overflowing the header on mobile and pushing this button (and its dropdown,
            anchored to it) off the right edge of the screen. The dropdown panel below still
            repeats all of this, so nothing is lost by collapsing it here. */}
        <div className="hidden sm:flex text-left flex-col gap-1 min-w-0 max-w-[10rem]">
          <div className="flex items-center gap-2 min-w-0">
            <div className="text-xs font-medium truncate min-w-0">
              {displayName}
            </div>
            {isPro && (
              <span className="text-xs px-2 py-0.5 bg-purple-600/20 text-purple-400 rounded border border-purple-600/30 flex-shrink-0 whitespace-nowrap">
                {planLabel}
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500 truncate">{email}</div>
        </div>
        <svg className="w-4 h-4 flex-shrink-0 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isDropdownOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-gray-700 rounded-md shadow-lg z-50 border border-gray-600">
          <div className="py-1">
            <div className="px-4 py-2 border-b border-gray-600">
              <div className="text-sm font-medium text-white">{displayName}</div>
              <div className="text-xs text-gray-400">{email}</div>
              {isPro && (
                <div className="mt-1">
                  <span className="text-xs px-2 py-0.5 bg-purple-600/20 text-purple-400 rounded border border-purple-600/30">
                    {planLabel}
                  </span>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={handleAccountClick}
              className={itemClass}
            >
              <CreditCardIcon className="h-4 w-4 flex-shrink-0" />
              Account &amp; Billing
            </button>

            <div className="my-1 border-t border-gray-600" />

            {/*
              The only way back to the welcome page once it has been hidden — without this row,
              "Don't show this again" would be a one-click decision with nothing to undo it. The
              `?show=1` is what makes the visit win over the saved preference; see
              welcome.container.tsx.

              This and the rows below are plain <Link>s so middle-click and cmd-click open them in
              a tab and leave the app where it is.
            */}
            <Link
              to="/welcome?show=1"
              onClick={closeDropdown}
              className={itemClass}
              title="The walkthrough video and the setup guide"
            >
              <PlayCircleIcon className="h-4 w-4 flex-shrink-0" />
              Getting started
            </Link>

            {/*
              The public site is the only place the Mapper download and the documentation live, and
              the app header has no room for either. Without these the signed-in app is a dead end:
              every route it draws is behind ProtectedRoute, so a user who wanted the desktop
              companion had to know the URL or sign out.
            */}
            <Link to="/docs" onClick={closeDropdown} className={itemClass}>
              <BookIcon className="h-4 w-4 flex-shrink-0" />
              Documentation
            </Link>

            <Link to="/downloads" onClick={closeDropdown} className={itemClass}>
              <DownloadIcon className="h-4 w-4 flex-shrink-0" />
              Download Mapper
            </Link>

            <Link to="/" onClick={closeDropdown} className={itemClass}>
              <HomeIcon className="h-4 w-4 flex-shrink-0" />
              CensorMe home
            </Link>

            <div className="my-1 border-t border-gray-600" />

            <button
              type="button"
              onClick={handleSignOut}
              className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-sm text-red-400 hover:bg-gray-600 transition-colors cursor-pointer"
            >
              <LogOutIcon className="h-4 w-4 flex-shrink-0" />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;